import { action, computed, makeObservable, observable } from "mobx";
import { INSTRUMENT_NAME, ServiceName } from "../../constants";
import { AboutServiceI } from "../../interfaces";

export class PageStore {
	@observable service: ServiceName = ServiceName.School;
	@observable instrument: INSTRUMENT_NAME = INSTRUMENT_NAME.GUITAR;
	@observable current_service: "beginner" | "advanced" | "professional" = "beginner";

	constructor() {
		makeObservable(this);
	}

	@action.bound
	setCurrentService(value: "beginner" | "advanced" | "professional") {
		this.current_service = value;
	}

	@action.bound
	setService(value: ServiceName) {
		this.service = value;
	}

	@action.bound
	setInstrument(value: INSTRUMENT_NAME) {
		this.instrument = value;
	}

	@computed
	get currentServiceInstrument(): string {
		return `${this.service}-${this.instrument}`;
	}

	@computed
	get title(): string {
		const title: { [key: string]: string } = {
			"school-saxophone": "Let's start your saxophone<br/> playing together",
			"school-keyboard": "Let's start your keyboard<br/> playing together",
			"school-guitar": "Let's start your music<br/> together",

			"college-saxophone": "Take your saxophone<br/> playing to the next level",
			"college-keyboard": "Take your keyboard playing to<br/> the next level",
			"college-guitar": "Take your guitar playing to<br/> the next level",

			"university-saxophone": "Become a saxophone<br/> professional",
			"university-keyboard": "Become a keyboard<br/> professional",
			"university-guitar": "Become a guitar<br/> professional",
		};

		return title[this.currentServiceInstrument] || "Not found";
	}

	@computed
	get subTitle() {
		const subTitle: { [key: string]: string } = {
			"school-saxophone": "Your Saxophone  |  The Internet  |  30+ min a day",
			"school-keyboard": "Your Keyboard  |  The Internet  |  30+ min a day",
			"school-guitar": "Your Guitar  |  The Internet  |  30+ min a day",

			"college-saxophone": "Your Saxophone  |  The Internet  |  45+ min a day",
			"college-keyboard": "Your Keyboard  |  The Internet  |  45+ min a day",
			"college-guitar": "Your Guitar  |  The Internet  |  45+ min a day",

			"university-saxophone": "Your Saxophone  |  The Internet  |  60+ min a day",
			"university-keyboard": "Your Keyboard  |  The Internet  |  60+ min a day",
			"university-guitar": "Your Guitar  |  The Internet  |  60+ min a day",
		};

		return subTitle[this.currentServiceInstrument] || "Not found";
	}

	@computed
	get header() {
		const headers: { [key: string]: string } = {
			"school-saxophone": "This saxophone beginner is for you if:",
			"school-keyboard": "This keyboard beginner is for you if:",
			"school-guitar": "This guitar beginner is for you if:",

			"college-saxophone": "This saxophone advanced is for you if:",
			"college-keyboard": "This keyboard advanced is for you if:",
			"college-guitar": "This guitar advanced is for you if:",

			"university-saxophone": "This saxophone professional is for you if:",
			"university-keyboard": "This keyboard professional is for you if:",
			"university-guitar": "This guitar professional is for you if:",
		};

		return headers[this.currentServiceInstrument] || "";
	}

	@computed
	get columns() {
		const columns: { [key: string]: Array<string> } = {
			"school-guitar": [
				"You already know ho to hold the guitar in your hands, but understand that it is not enough.",
				"You can play several chords and want to move further.",
				"You want to play your favorite songs and compose your own ones.",
			],
			"school-keyboard": [
				"You already know ho to hold the guitar in your hands, but understand that it is not enough.",
				"You can play several chords and want to move further.",
				"You want to play your favorite songs and compose your own ones.",
			],
			"school-saxophone": [
				"You already know ho to hold the guitar in your hands, but understand that it is not enough.",
				"You can play several chords and want to move further.",
				"You want to play your favorite songs and compose your own ones.",
			],

			"college-guitar": [
				"You want to play the guitar more consciously and technically",
				"You are thinking about obtaining systematic knowledge",
				"You strive to create new music using new methods and ways",
			],
			"college-keyboard": [
				"You want to play the keyboard more consciously and technically",
				"You are thinking about obtaining systematic knowledge",
				"You strive to create new music using new methods and ways",
			],
			"college-saxophone": [
				"You want to play the saxophone more consciously and technically",
				"You are thinking about obtaining systematic knowledge",
				"You strive to create new music using new methods and ways",
			],

			"university-guitar": [
				"Your guitar level is alreday high enough, but you feel that you've hit the сeiling and can't move further to become a true professional",
				"You are willing to enhance your theoretical and practical guitar knowledge to the maximum extent",
				"You want to learn how to instantly improvise and compose absolutely unique music",
			],
			"university-keyboard": [
				"Your keyboard level is alreday high enough, but you feel that you've hit the сeiling and can't move further to become a true professional",
				"You are willing to enhance your theoretical and practical keyboard knowledge to the maximum extent",
				"You want to learn how to instantly improvise and compose absolutely unique music",
			],
			"university-saxophone": [
				"Your saxophone level is alreday high enough, but you feel that you've hit the сeiling and can't move further to become a true professional",
				"You are willing to enhance your theoretical and practical saxophone knowledge to the maximum extent",
				"You want to learn how to instantly improvise and compose absolutely unique music",
			],
		};
		return columns[this.currentServiceInstrument] || [];
	}

	@computed
	get playingImage() {
		const data: { [key: string]: string } = {
			"school-saxophone": "playing-saxophone-school",
			"school-keyboard": "playing-keyboard-school",
			"school-guitar": "playing-guitar-school",

			"college-saxophone": "playing-saxophone-college",
			"college-keyboard": "playing-keyboard-college",
			"college-guitar": "playing-guitar-college",

			"university-saxophone": "playing-saxophone-university",
			"university-keyboard": "playing-keyboard-university",
			"university-guitar": "playing-guitar-university",
		};

		return data[this.currentServiceInstrument] || "";
	}

	@computed
	get headerPlaying() {
		const data: { [key: string]: string } = {
			beginner: "The beginning of a bright music journey ",
			advanced: "Pursuing the bright music future",
			university: "Pursuing the bright music future",
		};

		return data[this.service] || "";
	}

	@computed
	get textPlaying() {
		const data: { [key: string]: Array<string> } = {
			"school-saxophone": [
				"MUSICABINET | SAXOPHONE BEGINNER is made for those, who are eager to get high quality fundamental music education as fast as possible.",
				"It is for those, who want to invest their time & money into the future smartly without having to spend the last dime on it. ",
			],
			"school-keyboard": [
				"MUSICABINET | KEYS BEGINNER is made for those, who are eager to get high quality fundamental music education as fast as possible.",
				"It is for those, who want to invest their time & money into the future smartly without having to spend the last dime on it. ",
			],
			"school-guitar": [
				"MUSICABINET | GUITAR BEGINNER is made for those, who are eager to get high quality fundamental music education as fast as possible.",
				"It is for those, who want to invest their time & money into the future smartly without having to spend the last dime on it. ",
			],

			"college-saxophone": [
				"MUSICABINET | SAXOPHONE ADVANCED was created for those who want to get a fundamental musical education quickly and efficiently.",
				"This is a story for someone who is ready to wisely invest their time in their future, without giving up the last shirt.",
			],
			"college-keyboard": [
				"MUSICABINET | KEYBOARD ADVANCED was created for those who want to get a fundamental musical education quickly and efficiently.",
				"This is a story for someone who is ready to wisely invest their time in their future, without giving up the last shirt.",
			],
			"college-guitar": [
				"MUSICABINET | GUITAR ADVANCED was created for those who want to get a fundamental musical education quickly and efficiently.",
				"This is a story for someone who is ready to wisely invest their time in their future, without giving up the last shirt.",
			],

			"university-saxophone": [
				"MUSICABINET | SAXOPHONE PROFESSIONAL is made for those, who are striving to get the most out of fundamental music education in a systematic way.",
				"It is for those, who want to invest time & money into the future smartly in order to fullfill the dream of becoming a true professional.",
			],
			"university-keyboard": [
				"MUSICABINET | KEYBOARD PROFESSIONAL is made for those, who are striving to get the most out of fundamental music education in a systematic way.",
				"It is for those, who want to invest time & money into the future smartly in order to fullfill the dream of becoming a true professional.\n",
			],
			"university-guitar": [
				"MUSICABINET | GUITAR PROFESSIONAL is made for those, who are striving to get the most out of fundamental music education in a systematic way.",
				"It is for those, who want to invest time & money into the future smartly in order to fullfill the dream of becoming a true professional.",
			],
		};

		return data[this.currentServiceInstrument] || [];
	}

	@computed
	get familiarHeader() {
		const data: { [key: string]: string } = {
			beginner: "Get familiar with MUSICABINET | BEGINNER within 14 days",
			advanced: "Get familiar with MUSICABINET | ADVANCED within 14 days",
			university: "Get familiar with MUSICABINET | PROFESSIONAL within 14 days",
		};

		return data[this.service] || "";
	}

	@computed
	get grandChartId() {
		const data: { [key: string]: number } = {
			"school-saxophone": 4,
			"school-keyboard": 2,
			"school-guitar": 1,

			"college-saxophone": 6,
			"college-keyboard": 5,
			"college-guitar": 3,

			"university-saxophone": 9,
			"university-keyboard": 7,
			"university-guitar": 8,
		};

		return data[this.currentServiceInstrument] || 0;
	}

	@computed
	get aboutService() {
		const data: AboutServiceI = {
			beginner: [
				{
					count: 3,
					title: "courses",
					description: "Consists of 3 courses.  There are 6 modules in each course.",
				},
				{
					count: 149,
					title: "lessons",
					description: "A total of 149 lessons for studying music theory and practical exercises",
				},
				{
					count: 45,
					title: "backing tracks",
					description: "Practice to consolidate the knowledge gained or just for fun :-)",
				},
			],
			advanced: [
				{
					count: 4,
					title: "courses",
					description: "Consists of 4 courses from simple to complex. Each course includes 4 modules.",
				},
				{
					count: 192,
					title: "lessons",
					description: "A total of 192 lessons for learning music theory and practice",
				},
				{
					count: 75,
					title: "backing tracks",
					description: "Practice to consolidate the knowledge gained or just for fun :-)",
				},
			],
			university: [
				{
					count: 7,
					title: "courses",
					description:
						"Consists of 7 courses (from simple to complex).  There are 5 educational modules and 2 improvisational modules in each course.",
				},
				{
					count: 1001,
					title: "lessons",
					description: "A total of 1001 lessons for studying music theory and practical exercises",
				},
				{
					count: "300+",
					title: "backing tracks",
					description: "Practice to consolidate the knowledge gained to become a real professional",
				},
			],
		};

		return data[this.service] || [];
	}

	@computed
	get gadgetImage() {
		const paths: { [key: string]: string } = {
			beginner: "/images/gadjects/school-gadjects",
			advanced: "/images/gadjects/college-gadjects",
			university: "/images/gadjects/university-gadjects",
		};

		return paths[this.service] || "";
	}

	@computed
	get currentServiceMap(): ServiceName {
		const mapping = {
			beginner: ServiceName.School,
			advanced: ServiceName.College,
			professional: ServiceName.University,
		};

		return mapping[this.current_service];
	}
}
